import { getProperties } from '@/api/backend/registry';
import { PropertyListType, PropertyOwnershipCode } from '@/api/backend/types';
import { RegistryStatusBadge } from '@/components/registry-status-badge';
import { ResponsiveDataView } from '@/components/responsive-data-view';
import { PROPERTY_TYPE_CODES, ZONE_CODES } from '@/lib/constants';
import { formatIsoDate, getLocaleObject } from '@/lib/utils';
import {
  Card,
  DataTable,
  DataTableColumnType,
  DataTableFilterConfig,
  DataTableSortConfig,
  Link,
  SearchInput,
} from '@opaz/ui-components';
import { useQuery } from '@tanstack/react-query';
import { useScrollToSection } from '@/hooks/use-scroll-to-section';
import { DASHBOARD_SECTIONS } from '@/lib/dashboard-sections';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const PropertiesCard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  useScrollToSection(DASHBOARD_SECTIONS.properties);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortConfig, setSortConfig] = useState<DataTableSortConfig | null>(null);
  const [filterConfig, setFilterConfig] = useState<DataTableFilterConfig | null>(null);
  const [tempNameOrPlot, setTempNameOrPlot] = useState('');
  const [nameOrPlot, setNameOrPlot] = useState('');
  const [tempOwnerCrNumber, setTempOwnerCrNumber] = useState('');
  const [ownerCrNumber, setOwnerCrNumber] = useState('');

  const { data: page, isLoading } = useQuery({
    queryKey: ['properties', currentPage, sortConfig, filterConfig, nameOrPlot, ownerCrNumber],
    queryFn: () => {
      const zoneFilter = filterConfig?.zone;
      const propertyTypeFilter = filterConfig?.propertyType;
      const ownershipFilter = filterConfig?.ownership;

      return getProperties({
        page: currentPage,
        size: 5,
        nameOrPlot: nameOrPlot || undefined,
        ownerCrNumber: ownerCrNumber || undefined,
        zoneCode: zoneFilter?.type === 'select' ? (zoneFilter.value as string) : undefined,
        propertyTypeCode:
          propertyTypeFilter?.type === 'select'
            ? (propertyTypeFilter.value as PropertyListType['propertyTypeCode'])
            : undefined,
        ownershipCode:
          ownershipFilter?.type === 'select' ? (ownershipFilter.value as PropertyOwnershipCode) : undefined,
        sort: sortConfig ? `${sortConfig.key},${sortConfig.direction}` : undefined,
      });
    },
  });

  const columns: DataTableColumnType<PropertyListType>[] = [
    {
      title: t('properties.list.table.columns.registryNumber'),
      sortKey: 'recordNumber',
      cellClassName: 'whitespace-nowrap',
      render: (record: PropertyListType) => (
        <Link
          onClick={() => {
            navigate({
              to: '/registry/properties/$propertyId',
              params: { propertyId: record.registryNumber },
              search: { version: undefined },
            });
          }}
          className="w-[120px] text-start"
        >
          {record.registryNumber}
        </Link>
      ),
    },
    {
      title: t('properties.list.table.columns.propertyName'),
      render: (record: PropertyListType) => record.propertyName ?? '—',
    },
    {
      title: t('properties.list.table.columns.propertyType'),
      render: (record: PropertyListType) =>
        t([`backend.codes.${record.propertyTypeCode}` as unknown as TemplateStringsArray, 'common.unknown']),
      filter: {
        type: 'select',
        key: 'propertyType',
        options: PROPERTY_TYPE_CODES.map((code) => ({
          label: t(`backend.codes.${code}`),
          value: code,
        })),
      },
    },
    {
      title: t('properties.list.table.columns.units'),
      render: (record: PropertyListType) => record.countTotal,
      align: 'end',
      headerClassName: 'whitespace-nowrap',
    },
    {
      title: t('properties.list.table.columns.zone'),
      render: (record: PropertyListType) => t(`common.zoneCode.${record.zoneCode}`),
      filter: {
        type: 'select',
        key: 'zone',
        options: ZONE_CODES.map((zoneCode) => ({
          label: t(`common.zoneCode.${zoneCode}`),
          value: zoneCode,
        })),
      },
    },
    {
      title: t('properties.list.table.columns.status'),
      sortKey: 'entries.recordStatus',
      align: 'end',
      render: (record: PropertyListType) => <RegistryStatusBadge statusCode={record.statusCode} />,
    },
  ];

  const applyNameOrPlotSearch = () => {
    setNameOrPlot(tempNameOrPlot);
    setCurrentPage(0);
  };

  const applyOwnerCrNumberSearch = () => {
    setOwnerCrNumber(tempOwnerCrNumber);
    setCurrentPage(0);
  };

  return (
    <Card id={DASHBOARD_SECTIONS.properties} className="w-full scroll-mt-24 p-5 shadow-lg border-none">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold">{t('properties.list.title')}</h2>
      </div>

      <div className="flex flex-col gap-3 mb-5 sm:flex-row">
        <SearchInput
          className="flex-1 max-w-sm"
          placeholder={t('properties.list.filter.nameOrPlot.placeholder')}
          disabled={isLoading}
          onChange={(e) => setTempNameOrPlot(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              applyNameOrPlotSearch();
            }
          }}
          value={tempNameOrPlot}
          onClear={() => {
            setTempNameOrPlot('');
            setNameOrPlot('');
            setCurrentPage(0);
          }}
          onSearchSubmit={applyNameOrPlotSearch}
        />

        <SearchInput
          className="flex-1 max-w-sm"
          placeholder={t('properties.list.filter.ownerCrNumber.placeholder')}
          disabled={isLoading}
          onChange={(e) => setTempOwnerCrNumber(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              applyOwnerCrNumberSearch();
            }
          }}
          value={tempOwnerCrNumber}
          onClear={() => {
            setTempOwnerCrNumber('');
            setOwnerCrNumber('');
            setCurrentPage(0);
          }}
          onSearchSubmit={applyOwnerCrNumberSearch}
        />
      </div>

      <ResponsiveDataView
        columns={columns}
        data={page?.content?.length ? [...page.content] : []}
        isLoading={isLoading}
        emptyMessage={t('properties.list.emptyFilterComponent.title')}
        currentPage={currentPage + 1}
        totalPages={page?.totalPages || 0}
        onPageChange={(page) => setCurrentPage(page - 1)}
        getRowKey={(record) => record.registryNumber}
        filterConfig={filterConfig}
        onFilterChange={(config) => {
          setCurrentPage(0);
          setFilterConfig(config);
        }}
      >
        <DataTable
          locale={getLocaleObject(i18n.language)}
          columns={columns}
          dataSource={page?.content?.length ? [...page.content] : []}
          isLoading={isLoading}
          currentPage={currentPage + 1}
          totalPages={page?.totalPages || 0}
          onPageChange={(page) => setCurrentPage(page - 1)}
          onSortChange={setSortConfig}
          onFilterChange={(config) => {
            setCurrentPage(0);
            setFilterConfig(config);
          }}
          dateFormatter={(value: Date) => formatIsoDate(value.toISOString(), i18n.language)}
          translations={{
            apply: t('common.table.filter.apply'),
            clear: t('common.table.filter.clear'),
            from: t('common.table.filter.from'),
            clearAllFilters: t('common.table.filter.clearAllFilters'),
            noData: t('properties.list.emptyFilterComponent.title'),
            nextPage: t('common.table.next'),
            previousPage: t('common.table.previous'),
          }}
        />
      </ResponsiveDataView>
    </Card>
  );
};
